import React, { forwardRef } from 'react';
import { Placement, PopupMenu } from 'shared/components/ui/popup-menu';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { InteractionType } from 'shared/types/data-type';
import { Task, UpdateTask } from 'shared/types/task';

import { CompleteList } from './complete-list';
import { DateSubMenu } from './date-sub-menu';
import { DeadlineDeadlineSubMenu } from './deadline-deadline-sub-menu';
import { EditList } from './edit-list';
import { LifeCycleList } from './life-cycle-list';
import { LinkedItemSubMenu } from './linked-item-sub-menu';
import { PrioritySubMenu } from './priority-sub-menu';
import { ReminderTimeSubMenu } from './reminder-time-sub-menu';
import { RestoreList } from './restore-list';
import { TagsSubMenu } from './tags-sub-menu';
import { MenuGroups, SubMenu } from './types';

const itemRefsBase: Record<MenuGroups, Map<number, HTMLButtonElement>> = {
  [MenuGroups.Complete]: new Map<number, HTMLButtonElement>(),
  [MenuGroups.Edit]: new Map<number, HTMLButtonElement>(),
  [MenuGroups.Lifecycle]: new Map<number, HTMLButtonElement>(),
  [MenuGroups.Restore]: new Map<number, HTMLButtonElement>(),
};

enum Direction {
  Next = 'next',
  Previous = 'previous',
}

export type TaskCardMultiSelectContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside' | 'position' | 'interactionType'
> & {
  tasks: Task[];
  onClose: () => void;
  initialActiveSubMenu?: SubMenu;
  onUpdate?: (task: Task, updates: UpdateTask) => void;
  onDelete?: (tasks: Task[]) => void;
  onDuplicate?: (tasks: Task[]) => void;
  hideComplete?: boolean;
  hideEdit?: boolean;
};

export const TaskCardMultiSelectContextMenu = forwardRef<
  HTMLDivElement,
  TaskCardMultiSelectContextMenuProps
>(
  (
    {
      tasks,
      onClose,
      initialActiveSubMenu,
      onUpdate,
      onDelete,
      hideComplete,
      hideEdit,
      ...rest
    },
    ref,
  ) => {
    const [openSubMenu, setOpenSubMenu] = React.useState<SubMenu | undefined>(
      initialActiveSubMenu,
    );
    const itemRefs = React.useRef(itemRefsBase);

    const menuProps = {
      ...rest,
      position: 'bottom-start' as Placement,
      interactionType: InteractionType.Task,
      onOutside: onClose,
    };

    const registerItem = React.useCallback(
      (group: MenuGroups, index: number, element: HTMLButtonElement | null) => {
        if (element) {
          itemRefs.current[group].set(index, element);
          return;
        }

        itemRefs.current[group].delete(index);
      },
      [],
    );

    const focusItem = React.useCallback((direction: Direction) => {
      const allRefs = [
        ...Array.from(itemRefs.current.complete.entries()),
        ...Array.from(itemRefs.current.edit.entries()),
        ...Array.from(itemRefs.current.lifecycle.entries()),
        ...Array.from(itemRefs.current.restore.entries()),
      ].map(([_, ref]) => ref);

      if (!allRefs.length) {
        return;
      }

      const currentRef = document.activeElement as HTMLButtonElement;
      const currentIndex = allRefs.indexOf(currentRef);

      // If nothing focused, focus first item
      if (currentIndex === -1) {
        allRefs[0]?.focus();
        return;
      }

      const nextPos =
        (direction === Direction.Next
          ? currentIndex + 1
          : currentIndex - 1 + allRefs.length) % allRefs.length;
      allRefs[nextPos]?.focus();
    }, []);

    useKeysDown(
      ['ArrowDown'],
      (e) => {
        if (openSubMenu) {
          return;
        }
        e.stopPropagation();
        e.preventDefault();
        focusItem(Direction.Next);
      },
      { capture: true },
    );

    useKeysDown(
      ['ArrowUp'],
      (e) => {
        if (openSubMenu) {
          return;
        }
        e.stopPropagation();
        e.preventDefault();
        focusItem(Direction.Previous);
      },
      { capture: true },
    );

    useKeysDown(
      ['KeyP'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.Priority);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['KeyR'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.ReminderTime);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['KeyG'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.LinkedItem);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['KeyD'],
      (e) => {
        if (e.shiftKey) {
          e.preventDefault();
          setOpenSubMenu(SubMenu.DeadlineDeadline);
          return;
        }
        if (e.metaKey || e.ctrlKey || e.altKey) {
          return;
        }
        e.preventDefault();
        setOpenSubMenu(SubMenu.Date);
      },
      { ignoreInputFields: true, otherKeysInCombinationAllowed: true },
    );

    useKeysDown(
      ['KeyT'],
      (e) => {
        e.preventDefault();
        setOpenSubMenu(SubMenu.Tags);
      },
      { ignoreInputFields: true },
    );

    useKeysDown(
      ['Escape'],
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        if (openSubMenu) {
          if (initialActiveSubMenu) {
            onClose();
            return;
          }

          setOpenSubMenu(undefined);
          return;
        }

        onClose();
      },
      { capture: true, ignoreInputFields: false },
    );

    React.useEffect(() => {
      if (initialActiveSubMenu) {
        setOpenSubMenu(initialActiveSubMenu);
      }
    }, [initialActiveSubMenu]);

    const hasDeletedTask = tasks.some((task) => !!task.deletedAt);

    return (
      <>
        {!openSubMenu && (
          <PopupMenu ref={ref} {...menuProps}>
            {hasDeletedTask && (
              <RestoreList
                tasks={tasks}
                onClose={onClose}
                registerItem={registerItem}
                registerGroup={MenuGroups.Restore}
              />
            )}
            {!hideComplete && (
              <CompleteList
                tasks={tasks}
                onClose={onClose}
                registerItem={registerItem}
                registerGroup={MenuGroups.Complete}
              />
            )}
            {!hideEdit && (
              <EditList
                tasks={tasks}
                openSubMenu={setOpenSubMenu}
                registerItem={registerItem}
                registerGroup={MenuGroups.Edit}
              />
            )}
            {!hasDeletedTask && (
              <LifeCycleList
                tasks={tasks}
                onClose={onClose}
                registerItem={registerItem}
                registerGroup={MenuGroups.Lifecycle}
                onDelete={onDelete}
                onDuplicate={rest.onDuplicate}
                hasBottomBorder={hasDeletedTask}
              />
            )}
          </PopupMenu>
        )}

        {openSubMenu === SubMenu.Priority && (
          <PrioritySubMenu
            tasks={tasks}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.LinkedItem && (
          <LinkedItemSubMenu
            tasks={tasks}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.DeadlineDeadline && (
          <DeadlineDeadlineSubMenu
            tasks={tasks}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.ReminderTime && (
          <ReminderTimeSubMenu
            tasks={tasks}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.Date && (
          <DateSubMenu
            tasks={tasks}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}

        {openSubMenu === SubMenu.Tags && (
          <TagsSubMenu
            tasks={tasks}
            onClose={onClose}
            {...menuProps}
            onUpdate={onUpdate}
          />
        )}
      </>
    );
  },
);
